import React, { useState, useEffect } from 'react';
import './Products.css';
import iconBack from '../../assets/icon-back-2.svg';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { EndRecord } from '../../types/products';

interface ProductsProps {
  onBack: () => void;
}

const Products: React.FC<ProductsProps> = ({ onBack }) => {
  const { customer } = useAuth();
  const [records, setRecords] = useState<EndRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecords = async () => {
      if (!customer) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('end_records')
          .select(`
            *,
            phone_models (*),
            internet_products (*),
            tv_products (*)
          `)
          .eq('customer_id', customer.id)
          .eq('end_type', '가입완료')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('가입상품 조회 실패:', error);
        } else {
          setRecords(data || []);
        }
      } catch (error) {
        console.error('가입상품 조회 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, [customer]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear().toString().slice(2);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  const getProductName = (record: EndRecord) => {
    if (record.subscription_type === 'phone' && record.phone_models) {
      return record.phone_models.model_name;
    }
    if (record.subscription_type === 'internet' && record.internet_products) {
      return record.internet_products.title;
    }
    if (record.subscription_type === 'tv' && record.tv_products) {
      return record.tv_products.title;
    }
    return '상품 정보 없음';
  };

  const getProductImage = (record: EndRecord) => {
    if (record.subscription_type === 'phone' && record.phone_models?.image_url) {
      return record.phone_models.image_url;
    }
    return null;
  };

  const getProductDetails = (record: EndRecord) => {
    if (record.subscription_type === 'phone') {
      return {
        line1: record.phone_color || '',
        line2: record.phone_capacity || ''
      };
    }
    if (record.subscription_type === 'internet' && record.internet_products) {
      return {
        line1: record.carrier,
        line2: `${record.internet_products.speed_value}${record.internet_products.speed_unit}`
      };
    }
    if (record.subscription_type === 'tv' && record.tv_products) {
      return {
        line1: record.carrier,
        line2: record.tv_products.description || ''
      };
    }
    return { line1: '', line2: '' };
  };

  return (
    <div className="products-container">
      <div className="products-header">
        <div className="products-header-wrapper">
          <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={onBack} />
          <h1 className="products-title">가입한상품</h1>
          <div className="header-icon"></div>
        </div>
      </div>

      <div className="products-content">
        <h2 className="products-page-title">가입한상품</h2>
        <div className="products-list">
          {loading ? (
            <div className="products-loading">로딩 중...</div>
          ) : records.length === 0 ? (
            <div className="products-empty">가입한 상품이 없습니다.</div>
          ) : (
            records.map((record) => {
              const details = getProductDetails(record);
              const imageUrl = getProductImage(record);
              return (
                <div key={record.id} className="product-card">
                  <div className="product-image">
                    {imageUrl ? (
                      <img src={imageUrl} alt={getProductName(record)} />
                    ) : (
                      <div className="product-image-placeholder">
                        {record.subscription_type === 'phone' ? '📱' :
                         record.subscription_type === 'internet' ? '🌐' : '📺'}
                      </div>
                    )}
                  </div>
                  <div className="product-info">
                    <h3 className="product-name">{getProductName(record)}</h3>
                    <div className="product-details">
                      <div className="product-specs">
                        {details.line1 && <p className="product-spec">{details.line1}</p>}
                        {details.line2 && <p className="product-spec">{details.line2}</p>}
                      </div>
                      <p className="product-date">가입일 {formatDate(record.created_at)}</p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Products;
